import React, { useState } from "react";
import { ClientTherapistRelationship } from "@/types/therapist";
import ClientCard from "./ClientCard";
import EmptyClientList from "./EmptyClientList";
import { useClientActivityTracker } from "./ClientActivityTracker";
import { getInitials, getAvatarColor } from "./ClientUtils";

interface ClientListProps {
  clients: ClientTherapistRelationship[] | undefined;
  isLoading: boolean;
  onClientSelect: (clientId: string) => void;
  onConnectClient: () => void;
  getClientDisplayName: (client?: ClientTherapistRelationship['client']) => string;
  getClientDetails: (client?: ClientTherapistRelationship['client']) => { company: string, fullName: string };
  formatDate: (dateString: string) => string;
  onRelationshipUpdate?: () => void;
}

const ClientList = ({
  clients,
  isLoading,
  onClientSelect,
  onConnectClient,
  getClientDisplayName,
  getClientDetails,
  formatDate,
  onRelationshipUpdate
}: ClientListProps) => {
  const { profileImages, getClientActivityStatus, isClientSharing } = useClientActivityTracker(clients);

  const handleRelationshipUpdate = () => {
    if (onRelationshipUpdate) {
      onRelationshipUpdate();
    }
  };

  return (
    <div className="space-y-4 animate-fadeIn">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {isLoading ? (
          <p>Loading clients...</p>
        ) : clients?.length === 0 ? (
          <EmptyClientList onConnectClient={onConnectClient} />
        ) : (
          clients
            ?.sort((a, b) => {
              // First sort by status (active first)
              if (a.status === 'inactive' && b.status !== 'inactive') return 1;
              if (a.status !== 'inactive' && b.status === 'inactive') return -1;
              // Then sort by creation date (newest first)
              return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            })
            .map((relationship: ClientTherapistRelationship, idx, arr) => {
              const clientDetails = getClientDetails(relationship.client);
              const clientName = getClientDisplayName(relationship.client);
              const initials = getInitials(clientName);
              const avatarColor = getAvatarColor(clientName);
              const activityStatus = getClientActivityStatus(relationship);
              const clientImage = profileImages[relationship.client_id] || null;
              const isShared = isClientSharing[relationship.client_id] || false;

              // Insert divider before the first inactive client
              const isFirstInactive = relationship.status === 'inactive' && (idx === 0 || arr[idx - 1].status !== 'inactive');

              return (
                <React.Fragment key={relationship.id}>
                  {isFirstInactive && (
                    <div className="col-span-full my-2">
                      <div className="border-t-2 border-dashed border-gray-300 w-full" />
                    </div>
                  )}
                  <ClientCard
                    relationship={relationship}
                    clientName={clientName}
                    clientDetails={clientDetails}
                    clientImage={clientImage}
                    initials={initials}
                    avatarColor={avatarColor}
                    activityStatus={activityStatus}
                    formatDate={formatDate}
                    onClientSelect={onClientSelect}
                    getClientDisplayName={getClientDisplayName}
                    isShared={isShared}
                    onRelationshipUpdate={handleRelationshipUpdate}
                  />
                </React.Fragment>
              );
            })
        )}
      </div>
    </div>
  );
};

export default ClientList;
